import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import color from "../../../Constants/colors";
import { DocumentCraftingType } from "../../../Models/documentCrafting";
import {
  useDocumentCraftingDocuments,
  useInsertDocumentCraftingDiagram,
} from "../../../Query/useDocumentCrafting";

/** A4 types with picture blocks (`bild-text` / `text-bild`) and a photos field. */
const TARGET_TYPES = [DocumentCraftingType.FLYER, DocumentCraftingType.CASE_STUDY];

interface Props {
  open: boolean;
  documentId: number;
  revisionId: number;
  onClose: () => void;
  /** The revise run of the target document started. */
  onStarted: (runId: number) => void;
}

/** Puts a saved diagram into an A4 document: the backend starts a revise run
 * of the chosen document that places the diagram's PNG in a picture block. */
const InsertDiagramDialog: React.FC<Props> = ({ open, documentId, revisionId, onClose, onStarted }) => {
  const { t } = useTranslation("documentCrafting");
  const [targetId, setTargetId] = useState<number | "">("");
  const documents = useDocumentCraftingDocuments({
    type: TARGET_TYPES,
    sort: "last_revision",
    order: "desc",
    page_size: 100,
  });
  const insert = useInsertDocumentCraftingDiagram();
  const targets = (documents.data?.documents ?? []).filter(
    (doc) => doc.permissions.can_edit && doc.latest_revision !== null,
  );

  const submit = () => {
    if (targetId === "") return;
    insert.mutate(
      { documentId, revisionId, targetDocumentId: targetId },
      { onSuccess: (result) => onStarted(result.run_id) },
    );
  };

  return (
    <Dialog open={open} onClose={insert.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("detail.insertDiagram.title")}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: color.gray600, mb: 2 }}>{t("detail.insertDiagram.text")}</Typography>
        {documents.isLoading ? (
          <CircularProgress size={20} />
        ) : targets.length === 0 ? (
          <Alert severity="info">{t("detail.insertDiagram.none")}</Alert>
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            label={t("detail.insertDiagram.target")}
            value={targetId}
            onChange={(event) => setTargetId(Number(event.target.value))}
          >
            {targets.map((doc) => (
              <MenuItem key={doc.id} value={doc.id}>
                {doc.title}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={insert.isPending}>
          {t("common.cancel")}
        </Button>
        <Button variant="contained" onClick={submit} disabled={targetId === "" || insert.isPending}>
          {insert.isPending ? <CircularProgress size={16} /> : t("detail.insertDiagram.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InsertDiagramDialog;
